import { CHANNEL_OPTS_FOR_TOPIC_PROVIDER } from "@app/analytics/constants/topics";
import {
  getFormattedEntityDate,
  getThreadOrChannelName,
} from "@app/analytics/TopicReport/utils";
import type { TopicEntity } from "@app/analytics/types";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { truncateAlongWordBoundary } from "@app/common/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { Column, Row } from "@rui/Stack";
import { Img, Span } from "@rui/styles";

import { CitationButton, CitationPopover, CitationPopoverText } from "./styles";

const CITATION_POPOVER_ID = "citation-popover";

const ProviderIcon = ({ provider }: { provider: TopicEntity["provider"] }) => {
  if (provider === "unknown") return null;
  const providerLogo = PRODUCTIVITY_PROVIDERS_BY_ID[provider].icon;
  return <Img src={providerLogo} height="10px" />;
};

const Citation = ({ entity }: { entity: TopicEntity }) => {
  const { anchorEl, onClick: onOpen, onClose, open } = useAnchorElement();

  const citation = entity.citations[0];

  if (!citation || (!entity.author?.displayName && !entity.startTime)) {
    return null;
  }

  const entityDate = getFormattedEntityDate(entity);
  const threadPrefix =
    CHANNEL_OPTS_FOR_TOPIC_PROVIDER[entity.provider]?.channelPrefix ?? "";
  const source = getThreadOrChannelName(citation);
  const truncatedCitationText = truncateAlongWordBoundary(citation.text, 200);

  return (
    <>
      <CitationButton
        aria-haspopup="true"
        aria-owns={open ? CITATION_POPOVER_ID : undefined}
        startIcon={<ProviderIcon provider={entity.provider} />}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      >
        {entity.author?.displayName}
        {entity.author?.displayName && entity.startTime && (
          <Span className="citation__divider">&nbsp;|&nbsp;</Span>
        )}
        {entityDate}
      </CitationButton>
      <CitationPopover id={CITATION_POPOVER_ID} open={open} anchorEl={anchorEl}>
        <Column spacing={0.5}>
          <Row variant="left" spacing={0.5}>
            <ProviderIcon provider={entity.provider} />
            {entity.author?.displayName && (
              <CitationPopoverText fontWeight={600}>
                {entity.author.displayName}
              </CitationPopoverText>
            )}
            <CitationPopoverText color="shades.neutral.500">
              {entityDate}
            </CitationPopoverText>
          </Row>
          {source && (
            <CitationPopoverText color="shades.neutral.500">
              {threadPrefix}
              {source}
            </CitationPopoverText>
          )}
          <CitationPopoverText variant="labelReg">
            {truncatedCitationText}
          </CitationPopoverText>
        </Column>
      </CitationPopover>
    </>
  );
};

export default Citation;
